import React, { useState } from 'react';
import { Stack, Typography, Button, Card, CardContent, CardHeader, CardActions } from '@mui/material';
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

import ContentConversionHelper from 'views/App/Portal/Policy/DraftManuals/helpers/contentConversionHelper';
import { translate as t } from 'hooks/useTranslations';
import { useDTBLibraryContext } from '../../context';
import { DTB, DTBContentBlock } from '../../../_types/dtb';
import SortableBlock from './SortableBlock';
import { ActionMenu } from 'components/_atoms';

const getEmptyBlock = (type: DTBContentBlock['contentType']): DTBContentBlock | null => {
  switch (type) {
    case 'CONTENT':
      return {
        content: '',
        contentEditable: '',
        contentType: 'CONTENT',
        title: '',
      };
    case 'VIDEO':
      return {
        content: '',
        contentEditable: '',
        contentType: 'VIDEO',
        title: '',
        contentData: {
          videoUrl: '',
        },
      };
    default:
      return null;
  }
};

const ManageContent: React.FC = () => {
  const { editedData, setEditedData } = useDTBLibraryContext();
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [newlyAddedBlockIndex, setNewlyAddedBlockIndex] = useState<number | null>(null);

  const handleSaveBlock = (index: number, block: DTBContentBlock) => {
    setEditedData((prev: typeof editedData) => {
      if (!prev) return null;
      const prevBlock = prev.contentBlocks[index];
      const newContentBlocks = [...prev.contentBlocks];
      let newBlock = { ...newContentBlocks[index] };

      if (block.contentType === 'CONTENT') {
        if (prevBlock.title !== block.title) {
          newBlock.title = block.title;
        }
        const contentForRender = ContentConversionHelper.contentForRender(block.content);
        if (prevBlock.content !== contentForRender) {
          newBlock.contentEditable = block.content;
          newBlock.content = contentForRender;
        }
      }

      if (block.contentType === 'VIDEO') {
        newBlock = block;
      }

      newContentBlocks[index] = newBlock;
      return {
        ...prev,
        contentBlocks: newContentBlocks,
      };
    });
  };

  const handleAddBlock = (type: DTBContentBlock['contentType']) => {
    setEditedData((prev: DTB) => {
      if (!prev) return null;
      const newBlock: DTBContentBlock | null = getEmptyBlock(type);
      if (!newBlock) return prev;
      const newIndex = prev.contentBlocks.length;
      setNewlyAddedBlockIndex(newIndex);
      return {
        ...prev,
        contentBlocks: [...prev.contentBlocks, newBlock],
      };
    });
  };

  const handleDeleteBlock = (index: number) => {
    setEditedData((prev: any) => {
      if (!prev) return null;
      const newContentBlocks = [...prev.contentBlocks];
      newContentBlocks.splice(index, 1);
      return {
        ...prev,
        contentBlocks: newContentBlocks,
      };
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setEditedData((prev: any) => {
      if (!prev) return null;
      const newContentBlocks = Array.from(prev.contentBlocks);
      const oldIndex = parseInt(active.id.toString().split('-')[1]);
      const newIndex = parseInt(over.id.toString().split('-')[1]);

      const [removed] = newContentBlocks.splice(oldIndex, 1);
      newContentBlocks.splice(newIndex, 0, removed);

      return {
        ...prev,
        contentBlocks: newContentBlocks,
      };
    });
  };

  if (!editedData) {
    return <Typography>Loading DTB data...</Typography>;
  }

  return (
    <Card className='lex-card'>
      <CardHeader
        title={t('dtbs.library.detail.content.title')}
        action={
          <Stack
            direction='row'
            spacing={2}
          >
            {editedData.contentBlocks.length >= 2 && (
              <Button
                variant='outlined'
                color='primary'
                size='small'
                onClick={() => setIsReorderMode(!isReorderMode)}
              >
                {isReorderMode ? t('common.done') : t('dtbs.library.detail.content.reorderSections')}
              </Button>
            )}
          </Stack>
        }
      />
      <CardContent>
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={editedData.contentBlocks.map((_, index) => `content-${index}`)}
            strategy={verticalListSortingStrategy}
          >
            <Stack spacing={4}>
              {editedData.contentBlocks.map((block, index) => (
                <SortableBlock
                  key={`content-${index}`}
                  block={block}
                  index={index}
                  onSave={handleSaveBlock}
                  isReorderMode={isReorderMode}
                  onDelete={handleDeleteBlock}
                  isNewlyAdded={newlyAddedBlockIndex === index}
                  onEditModeChange={(isEditMode) => {
                    if (!isEditMode && newlyAddedBlockIndex === index) {
                      setNewlyAddedBlockIndex(null);
                    }
                  }}
                  dtbLevel={editedData.level}
                />
              ))}
            </Stack>
          </SortableContext>
        </DndContext>
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end', paddingX: 4, paddingY: 3 }}>
        <ActionMenu
          id='block-action-menu'
          btnText={t('dtbs.library.detail.content.addContent')}
          btnProps={{
            size: 'small',
            variant: 'outlined',
          }}
          items={[
            {
              key: 'CONTENT',
              label: t('dtbs.library.detail.content.addOptions.content'),
              onClick: () => handleAddBlock('CONTENT'),
            },
            ...(editedData.level !== 'AGENCY'
              ? [
                  {
                    key: 'VIDEO',
                    label: t('dtbs.library.detail.content.addOptions.video'),
                    onClick: () => handleAddBlock('VIDEO'),
                  },
                ]
              : []),
          ]}
        />
      </CardActions>
    </Card>
  );
};

export default ManageContent;
